/**
 * About Us Section Component
 * Displays company information: Who We Are, Mission, Vision, and Corporate Values
 */

export interface AboutValue {
  title: string
  content: string
}

export interface AboutConfig {
  whoWeAre: {
    title: string
    content: string
    imageSrc: string
  }
  mission?: {
    title: string
    content: string
    imageSrc: string
  } | null
  vision?: {
    title: string
    content: string
    imageSrc: string
  } | null
  values: {
    title: string
    items: AboutValue[]
  }
}

export class About {
  private config: AboutConfig
  public element: HTMLElement

  constructor(config: AboutConfig) {
    this.config = config
    this.element = this.render()
  }

  /**
   * Get SVG icon for a value card
   */
  private getValueIcon(index: number): string {
    const icons = [
      // Respaldo Jurídico - scale/balance icon
      `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"/><path d="M3 7l9-4 9 4"/><path d="M3 7v4a3 3 0 0 0 6 0V7"/><path d="M15 7v4a3 3 0 0 0 6 0V7"/></svg>`,
      // Inclusión Financiera - users/people icon
      `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
      // Innovación Transparente - lightbulb icon
      `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg>`
    ]
    return icons[index] || icons[0]
  }

  /**
   * Render the about us section
   */
  render(): HTMLElement {
    const section = document.createElement('section')
    section.id = 'about'
    section.className = 'about-section section'

    section.innerHTML = `
      <div class="container">
        <!-- Who We Are -->
        <div class="about-content-block">
          <div class="about-text">
            <h2 class="about-title" data-i18n="about.whoWeAre.title">
              ${this.config.whoWeAre.title}
            </h2>
            <p class="about-description" data-i18n="about.whoWeAre.content">
              ${this.config.whoWeAre.content}
            </p>
          </div>
          <div class="about-image">
            <img src="${this.config.whoWeAre.imageSrc}" alt="${this.config.whoWeAre.title}" loading="lazy">
          </div>
        </div>

        ${this.config.mission ? `
        <!-- Mission -->
        <div class="about-content-block about-content-block-reverse">
          <div class="about-text">
            <h2 class="about-title" data-i18n="about.mission.title">
              ${this.config.mission.title}
            </h2>
            <p class="about-description" data-i18n="about.mission.content">
              ${this.config.mission.content}
            </p>
          </div>
          <div class="about-image">
            <img src="${this.config.mission.imageSrc}" alt="${this.config.mission.title}" loading="lazy">
          </div>
        </div>
        ` : ''}

        ${this.config.vision ? `
        <!-- Vision -->
        <div class="about-content-block">
          <div class="about-text">
            <h2 class="about-title" data-i18n="about.vision.title">
              ${this.config.vision.title}
            </h2>
            <p class="about-description" data-i18n="about.vision.content">
              ${this.config.vision.content}
            </p>
          </div>
          <div class="about-image">
            <img src="${this.config.vision.imageSrc}" alt="${this.config.vision.title}" loading="lazy">
          </div>
        </div>
        ` : ''}

        <!-- Corporate Values -->
        <div class="about-values">
          <h2 class="about-values-title text-center" data-i18n="about.values.title">
            ${this.config.values.title}
          </h2>
          <div class="about-values-grid">
            ${this.config.values.items.map((value, index) => `
              <div class="value-card" data-index="${index}">
                <div class="value-card-icon">
                  ${this.getValueIcon(index)}
                </div>
                <h3 class="value-card-title" data-i18n="about.values.items.${index}.title">
                  ${value.title}
                </h3>
                <p class="value-card-content" data-i18n="about.values.items.${index}.content">
                  ${value.content}
                </p>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `

    return section
  }
}
